import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import { motion } from "framer-motion";
import { Handshake, DollarSign, Newspaper, MessageCircle, CheckCircle, Mail, Globe, MapPin, User } from "lucide-react";

export default function Contact() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [inquiryType, setInquiryType] = useState("general");

  const formik = useFormik({
    initialValues: { name: "", email: "", company: "", message: "", type: "general" },
    validationSchema: Yup.object({
      name: Yup.string().required("Required"),
      email: Yup.string().email("Invalid email").required("Required"),
      message: Yup.string().required("Required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        await addDoc(collection(db, "contacts"), {
          ...values,
          type: inquiryType,
          timestamp: new Date()
        });
        setIsSubmitted(true);
        resetForm();
        setInquiryType("general");
        setTimeout(() => setIsSubmitted(false), 5000);
      } catch (error) {
        console.error("Error saving form data: ", error);
        alert("An error occurred. Please try again.");
      }
    },
  });

  const founders = [
    {
      name: "Siddhant Sawant",
      role: "Technical Director & Co-Founder",
      bio: "Leading product development and technical architecture",
      email: "siddhant@morzetech.com",
      Icon: User
    },
    {
      name: "Akshatha Poojari",
      role: "Technical Director & Co-Founder",
      bio: "Driving engineering excellence and platform scalability",
      email: "akshatha.poojari01@gmail.com",
      Icon: User
    }
  ];

  const contactReasons = [
    {
      type: "partnership",
      title: "Strategic Partnerships",
      Icon: Handshake,
      desc: "Restaurants, chef associations, or culinary businesses interested in collaboration"
    },
    {
      type: "investor",
      title: "Investment Opportunities",
      Icon: DollarSign,
      desc: "Angel investors or VCs interested in hospitality tech and marketplace platforms"
    },
    {
      type: "press",
      title: "Press & Media",
      Icon: Newspaper,
      desc: "Journalists covering hospitality tech, startups, or culinary innovation"
    },
    {
      type: "general",
      title: "General Inquiries",
      Icon: MessageCircle,
      desc: "Questions about our platform, services, or anything else"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Success Message */}
      {isSubmitted && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-xl z-50 flex items-center gap-2"
        >
          <CheckCircle className="w-5 h-5" />
          Message sent! We'll get back to you within 24 hours.
        </motion.div>
      )}

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Let's Connect</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Whether you're interested in partnership, investment, or just want to say hi, we'd love to hear from you.
          </p>
        </motion.div>

        {/* Inquiry Type Selection */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">What brings you here?</h2>
          <div className="grid md:grid-cols-4 gap-4">
            {contactReasons.map((reason) => (
              <motion.button
                key={reason.type}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setInquiryType(reason.type)}
                className={`p-6 rounded-xl border-2 transition-all text-left ${
                  inquiryType === reason.type
                    ? "border-orange bg-orange/10"
                    : "border-gray-200 bg-white hover:border-orange/50"
                }`}
              >
                <reason.Icon className="w-8 h-8 mb-3 text-orange" />
                <h3 className="font-bold text-gray-900 mb-2">{reason.title}</h3>
                <p className="text-sm text-gray-600">{reason.desc}</p>
              </motion.button>
            ))}
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-8 rounded-xl shadow-md"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a message</h2>
            <form onSubmit={formik.handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Your Name *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange focus:border-orange"
                />
                {formik.errors.name && (
                  <p className="mt-1 text-sm text-red-600">{formik.errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  onChange={formik.handleChange}
                  value={formik.values.email}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange focus:border-orange"
                />
                {formik.errors.email && (
                  <p className="mt-1 text-sm text-red-600">{formik.errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                  Company / Organization
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  onChange={formik.handleChange}
                  value={formik.values.company}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange focus:border-orange"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Your Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  onChange={formik.handleChange}
                  value={formik.values.message}
                  placeholder={
                    inquiryType === "investor" 
                      ? "Tell us about your investment thesis and areas of interest..."
                      : inquiryType === "partnership"
                      ? "Describe your business and how we might work together..."
                      : "How can we help you?"
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange focus:border-orange"
                ></textarea>
                {formik.errors.message && (
                  <p className="mt-1 text-sm text-red-600">{formik.errors.message}</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full bg-orange text-white py-3 px-6 rounded-lg hover:bg-orange-dark transition duration-300 font-medium"
              >
                Send Message →
              </button>
            </form>
          </motion.div>

          {/* Right Column */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-8"
          >
            {/* Current Status Banner */}
            <div className="bg-gradient-to-r from-orange/20 to-orange/5 border border-orange/30 rounded-xl p-6">
              <div className="flex items-start gap-3 mb-3">
                <CheckCircle className="w-6 h-6 text-orange flex-shrink-0 mt-1" />
                <h3 className="text-lg font-bold text-gray-900">Where We Are Now</h3>
              </div>
              <p className="text-gray-700 mb-4">
                Our platform, <strong>Compliments to the Chef</strong>, is live and actively scaling. We're in the growth and validation phase, expanding our user base and proving product-market fit.
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-white rounded-lg p-3">
                  <div className="font-bold text-orange">Growth & Validation</div>
                  <div className="text-gray-600">Active Phase</div>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <div className="font-bold text-orange">Scaling</div>
                  <div className="text-gray-600">User Base</div>
                </div>
              </div>
            </div>

            {/* Founders Section */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Meet Our Founding Team</h2>
              <p className="text-gray-600 mb-6 text-sm">
                Connect directly with our founders for strategic partnerships or investment discussions
              </p>
              
              <div className="space-y-4">
                {founders.map((founder, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ x: 3 }}
                    className="flex items-start p-4 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <founder.Icon className="w-8 h-8 mr-4 text-orange flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{founder.name}</h3>
                      <p className="text-orange text-sm font-medium mb-1">{founder.role}</p>
                      <p className="text-gray-600 text-xs mb-2">{founder.bio}</p>
                      <a
                        href={`mailto:${founder.email}`}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        {founder.email}
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Quick Contact */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Contact</h3>
              <div className="space-y-3">
                <p className="flex items-center text-gray-600">
                  <Mail className="w-5 h-5 mr-3 text-orange" />
                  <a href="mailto:hello@morzetech.com" className="hover:text-orange">
                    hello@morzetech.com
                  </a>
                </p>
                <p className="flex items-center text-gray-600">
                  <Globe className="w-5 h-5 mr-3 text-orange" />
                  <a href="https://www.complimentstothechef.co.uk/" target="_blank" rel="noopener noreferrer" className="hover:text-orange">
                    complimentstothechef.co.uk
                  </a>
                </p>
                <p className="flex items-center text-gray-600">
                  <MapPin className="w-5 h-5 mr-3 text-orange" />
                  Remote-First Team
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}